import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Filter, ShoppingCart, User, LogOut, Shield, Clock, Gavel, Tag, Star, Bell, BellRing, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${'https://shopblkmrkt.com'}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";
const DEFAULT_PRODUCT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/d742bfc9-e565-49d9-b972-9ec11d4b1acf/images/8bffc3d80961a69d81ff6049f0b0e3b52871da1a055b9513c572b6a653fd3377.png";

export default function Catalog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, logout, loading: authLoading, authResolved, hasToken, hasValidToken, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState(() => {
    // Try to load cached products for instant display
    const cached = sessionStorage.getItem('catalog_products_cache');
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        // Use cache if less than 60 seconds old
        if (Date.now() - timestamp < 60000) {
          return data;
        }
      } catch (e) {}
    }
    return [];
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(() => {
    // If we have cached products, don't show loading
    const cached = sessionStorage.getItem('catalog_products_cache');
    if (cached) {
      try {
        const { timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 60000) {
          return false;
        }
      } catch (e) {}
    }
    return true;
  });
  // Initialize hasAccess from sessionStorage for instant display on refresh
  const [hasAccess, setHasAccess] = useState(() => {
    return sessionStorage.getItem('blkmrkt_access') === 'granted';
  });
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || ''); // Input field value
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || ''); // Actual query used for API
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [filterBidding, setFilterBidding] = useState(searchParams.get('bidding') || 'all');
  const [filterStock, setFilterStock] = useState(searchParams.get('stock') || 'instock');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'default');
  
  // Pagination state - restore from sessionStorage on back navigation
  const ITEMS_PER_PAGE = 20;
  const [displayCount, setDisplayCount] = useState(() => {
    const saved = sessionStorage.getItem('catalogDisplayCount');
    return saved ? parseInt(saved, 10) : ITEMS_PER_PAGE;
  });
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Save display count to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('catalogDisplayCount', displayCount.toString());
  }, [displayCount]);
  
  // Restore scroll position on back navigation
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('catalogScrollPosition');
    if (savedScroll && products.length > 0) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll, 10));
      }, 100);
    }
  }, [products.length]);
  
  // Save scroll position before navigating away
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem('catalogScrollPosition', window.scrollY.toString());
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Restock notification subscriptions
  const [restockSubscriptions, setRestockSubscriptions] = useState([]);
  const [subscribingProduct, setSubscribingProduct] = useState(null);
  
  // Brand colors map
  const [brandColors, setBrandColors] = useState({});

  // Fetch brand colors
  useEffect(() => {
    const fetchBrandColors = async () => {
      try {
        const response = await axios.get(`${API}/brands/map`);
        setBrandColors(response.data || {});
      } catch (error) {
        // Silently fail
      }
    };
    fetchBrandColors();
  }, []);

  // Fetch user's restock subscriptions
  const fetchRestockSubscriptions = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/user/restock-subscriptions`, {
        headers: getAuthHeader()
      });
      setRestockSubscriptions(response.data.product_ids || []);
    } catch (error) {
      // Silently fail
    }
  }, [user, getAuthHeader]);

  // Subscribe to restock notifications
  const handleRestockSubscribe = async (e, productId, productName) => {
    e.preventDefault(); // Prevent navigation to product page
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please login to get notified');
      navigate('/login');
      return;
    }
    
    try {
      setSubscribingProduct(productId);
      await axios.post(
        `${API}/products/${productId}/notify-restock`,
        {},
        { headers: getAuthHeader() }
      );
      setRestockSubscriptions(prev => [...prev, productId]);
      toast.success(`You'll be notified when "${productName}" is back!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribingProduct(null);
    }
  };

  // Check access on mount - REQUIRE LOGIN
  // CRITICAL: Wait for authResolved before making any navigation decisions
  useEffect(() => {
    let isMounted = true;
    
    const checkAccess = async () => {
      // WAIT for auth to be resolved before checking anything
      if (!authResolved) {
        return;
      }
      
      // Check for token
      const tokenExists = hasValidToken();
      
      // No token = need to login
      if (!tokenExists) {
        navigate('/login');
        return;
      }
      
      // Check drop status from API
      try {
        const response = await axios.get(`${API}/drop/status`, { timeout: 5000 });
        
        if (!isMounted) return;
        
        // Drop is open - grant access
        if (response.data.is_open) {
          setHasAccess(true);
          sessionStorage.setItem('blkmrkt_access', 'granted');
          return;
        }
        
        // Drop is closed - check for access code
        if (response.data.access_code) {
          const storedCode = sessionStorage.getItem('blkmrkt_access_code');
          const hasTempAccess = sessionStorage.getItem('blkmrkt_temp_access') === 'true';
          
          if (hasTempAccess || storedCode === response.data.access_code) {
            setHasAccess(true);
            sessionStorage.setItem('blkmrkt_access', 'granted');
            return;
          }
        }
        
        // Drop is closed, no valid access - redirect to landing
        sessionStorage.removeItem('blkmrkt_access');
        navigate('/');
        
      } catch (error) {
        if (!isMounted) return;
        console.error('Failed to check drop status:', error);
        // On error, redirect to landing to be safe
        navigate('/');
      }
    };
    
    checkAccess();
    
    return () => {
      isMounted = false;
    };
  }, [navigate, authResolved, hasValidToken]);

  const fetchProducts = useCallback(async (forceRefresh = false) => {
    try {
      // Build cache key based on current filters + user BLKCRD status
      const blkcrdKey = userProfile?.has_blkcrd ? 'blkcrd' : (userProfile?.is_admin ? 'admin' : 'regular');
      const cacheKey = `catalog_${searchQuery}_${selectedCategory}_${filterBidding}_${filterStock}_${blkcrdKey}`;
      
      // Skip session cache for admin/BLKCRD (coming_soon items may differ from cached regular view)
      const canSeeComingSoon = userProfile?.is_admin || userProfile?.has_blkcrd;
      
      // Check session cache first (unless force refresh or admin/BLKCRD)
      if (!forceRefresh && !canSeeComingSoon) {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 30 seconds old
          if (Date.now() - timestamp < 30000) {
            setProducts(data);
            setLoading(false);
            return;
          }
        }
      }
      
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      if (filterBidding === 'true') params.append('is_bidding', 'true');
      if (filterBidding === 'false') params.append('is_bidding', 'false');
      if (filterStock === 'instock') params.append('in_stock', 'true');
      // Enable grouping by brand for catalog display
      params.append('group_by_brand', 'true');
      
      // Include auth header so backend can check BLKCRD/admin status for Coming Soon items
      const headers = getAuthHeader();
      const response = await axios.get(`${API}/products?${params.toString()}`, { headers });
      setProducts(response.data);
      setDisplayCount(ITEMS_PER_PAGE); // Reset pagination when filters change
      
      // Cache the result
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
      // Also save to global cache for instant page load
      sessionStorage.setItem('catalog_products_cache', JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, filterBidding, filterStock]);

  // Load more products (called by infinite scroll)
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    // Small delay for smooth UX
    setTimeout(() => {
      setDisplayCount(prev => prev + ITEMS_PER_PAGE);
      setLoadingMore(false);
    }, 100);
  }, [loadingMore]);

  // Sort products based on selected sort option
  // Coming soon items always appear at the top
  const sortedProducts = useMemo(() => {
    if (!products.length) return products;
    
    const comingSoon = products.filter(p => p.visibility === 'coming_soon');
    const regular = products.filter(p => p.visibility !== 'coming_soon');
    
    if (sortBy === 'default') return [...comingSoon, ...regular];
    
    const sorted = [...regular];
    switch (sortBy) {
      case 'price-low':
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-high':
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'a-z':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'z-a':
        sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      default:
        break;
    }
    return [...comingSoon, ...sorted];
  }, [products, sortBy]);

  // Get visible products (paginated) - must be defined before useEffect that uses hasMore
  const visibleProducts = sortedProducts.slice(0, displayCount);
  const hasMore = displayCount < sortedProducts.length;

  // Infinite scroll - Intersection Observer
  const loadMoreRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '200px' } // Trigger 200px before reaching the element
    );
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/products/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchProducts();
      fetchRestockSubscriptions();
    }
  }, [fetchProducts, fetchRestockSubscriptions, hasAccess]);

  // Sync filter state to URL for back/forward navigation
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (filterBidding !== 'all') params.set('bidding', filterBidding);
    if (filterStock !== 'all') params.set('stock', filterStock);
    if (sortBy !== 'default') params.set('sort', sortBy);
    
    // Only update if params actually changed to avoid infinite loops
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
    }
  }, [searchQuery, selectedCategory, filterBidding, filterStock, sortBy, searchParams, setSearchParams]);

  // Poll for auction status updates every 10 seconds
  useEffect(() => {
    if (!hasAccess) return;
    
    const pollAuctionStatus = async () => {
      try {
        const response = await axios.get(`${API}/auctions/status`);
        const { auctions } = response.data;
        
        if (!auctions || auctions.length === 0) return;
        
        // Create a map for quick lookup
        const auctionMap = {};
        auctions.forEach(a => { auctionMap[a.id] = a; });
        
        // Update products with new auction data
        setProducts(prev => prev.map(product => {
          const auctionData = auctionMap[product.id];
          if (auctionData) {
            return {
              ...product,
              current_bid: auctionData.current_bid,
              _timeRemaining: auctionData.time_remaining,
              _isEnded: auctionData.is_ended
            };
          }
          return product;
        }));
      } catch (error) {
        // Silently fail - this is just a background poll
      }
    };
    
    // Initial poll
    pollAuctionStatus();
    
    // Set up interval
    const interval = setInterval(pollAuctionStatus, 10000); // Every 10 seconds
    
    return () => clearInterval(interval);
  }, [hasAccess]);

  useEffect(() => {
    if (hasAccess) {
      fetchCategories();
    }
  }, [hasAccess]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Update the actual search query from input field - this triggers the API call
    setSearchQuery(searchInput);
    const params = new URLSearchParams();
    if (searchInput) params.set('search', searchInput);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (filterBidding !== 'all') params.set('bidding', filterBidding);
    if (filterStock !== 'all') params.set('stock', filterStock);
    setSearchParams(params);
  };

  // Helper to resolve image URLs - handles both relative and absolute URLs
  const resolveImageUrl = (url, size = null) => {
    if (!url || url.trim() === '') return DEFAULT_PRODUCT_IMAGE;
    
    // If it's a broken old preview URL, return default immediately
    if (url.includes('preview.emergentagent.com') && !url.includes('rare-deck-hub')) {
      console.warn('Broken preview URL detected:', url);
      return DEFAULT_PRODUCT_IMAGE;
    }
    
    // If already absolute URL with external domain, return as-is
    if ((url.startsWith('http://') || url.startsWith('https://')) && !url.includes('/api/images/')) {
      return url;
    }
    
    // If relative URL, prepend backend URL
    const backendBase = 'https://shopblkmrkt.com';
    let fullUrl = url.startsWith('http') ? url : `${backendBase}${url}`;
    
    // Add size parameter for thumbnails
    if (size && fullUrl.includes('/api/images/')) {
      fullUrl += `?size=${size}`;
    }
    
    return fullUrl;
  };

  const getProductImage = (product) => {
    // Use server-side medium thumbnail so the catalog grid doesn't pull down
    // multi-MB originals (causes browser OOM with many products).
    return resolveImageUrl(product.image_url, 'medium');
  };

  const formatTimeRemaining = (endTime) => {
    if (!endTime) return null;
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return 'ENDED';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}D ${hours % 24}H`;
    }
    return `${hours}H ${minutes}M`;
  };

  // Format auction end time for display (e.g., "ENDS 9:00 PM EST")
  const formatAuctionEndTime = (endTime) => {
    if (!endTime) return null;
    const end = new Date(endTime);
    // Format to Eastern Time
    const options = { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true,
      timeZone: 'America/New_York'
    };
    const timeStr = end.toLocaleTimeString('en-US', options).toUpperCase();
    return `ENDS ${timeStr} EST`;
  };

  // Helper to check if product is an ended auction that wasn't sold (buy_now_sold counts as sold)
  const isEndedAuction = (product) => {
    // Explicitly processed and not sold - these are UNAVAILABLE until re-auctioned
    if (product.auction_processed && product.auction_result && 
        product.auction_result !== 'sold' && product.auction_result !== 'buy_now_sold') {
      return true;
    }
    return false;
  };

  // Helper to check if this is a sold auction (was bidding, now processed as sold)
  const isSoldAuction = (product) => {
    return product.auction_processed && 
      (product.auction_result === 'sold' || product.auction_result === 'buy_now_sold');
  };

  // Helper to get auction end status text
  const getAuctionEndStatus = (product) => {
    // If stock is 0 and was an auction (has bid_end_time), it's sold out
    if (product.stock === 0 && (product.is_bidding || product.bid_end_time)) {
      return 'SOLD OUT';
    }
    if (!product.auction_processed) return null;
    
    switch (product.auction_result) {
      case 'no_bids':
        return 'RESERVE NOT MET'; // Always show RESERVE NOT MET, never NO BIDS
      case 'reserve_not_met':
        return 'RESERVE NOT MET';
      case 'sold':
        return 'SOLD OUT';
      case 'buy_now_sold':
        return 'SOLD OUT';
      default:
        return 'RESERVE NOT MET'; // Default to RESERVE NOT MET for ended auctions
    }
  };

  // Helper to check if product should show as unavailable (sold out OR ended auction OR sold auction)
  const isUnavailable = (product) => {
    return product.stock === 0 || isEndedAuction(product) || isSoldAuction(product);
  };

  // Don't render anything until we have access
  // If user had previous access (from sessionStorage), show immediately
  // Otherwise wait for auth to resolve and access to be verified
  const showLoading = !hasAccess && (!authResolved || authLoading);
  
  if (showLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono">VERIFYING ACCESS...</div>
      </div>
    );
  }
  
  // If auth resolved but no access, the useEffect will handle redirect
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono">VERIFYING ACCESS...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="fixed left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm" style={{ top: 'var(--banner-offset, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link 
              to="/cart" 
              className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link 
              to="/dashboard" 
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="dashboard-link"
            >
              <User className="w-5 h-5" />
            </Link>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                data-testid="admin-link"
              >
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <button 
              onClick={logout}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Page Title */}
        <h1 className="font-sans text-4xl md:text-5xl text-white mb-8 tracking-tighter" data-testid="catalog-title">
          CATALOG
        </h1>

        {/* Search & Filters - Compact layout */}
        <form onSubmit={handleSearch} className="mb-6 space-y-3 overflow-hidden">
          {/* Search input with clear button */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="SEARCH..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                className="w-full h-10 pl-10 pr-10 bg-zinc-900 border-zinc-800 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none uppercase"
                data-testid="search-input"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                    setSearchParams(new URLSearchParams());
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center bg-zinc-700 hover:bg-zinc-600 text-white rounded-full transition-colors"
                  data-testid="clear-search-btn"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <Button
              type="submit"
              className="h-10 px-4 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono text-xs font-bold rounded-none shadow-lg shadow-tiffany-500/20 flex-shrink-0"
              data-testid="search-btn"
            >
              <Search className="w-3 h-3 mr-1.5" />
              SEARCH
            </Button>
          </div>
          
          {/* Filter Row - Type filters + Sort */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="grid grid-cols-3 gap-2 flex-1">
              <button
                type="button"
                onClick={() => setFilterBidding(filterBidding === 'true' ? 'all' : 'true')}
                className={`h-8 font-mono text-[11px] rounded-none border transition-colors ${
                  filterBidding === 'true' 
                    ? 'bg-tiffany-500 text-black border-tiffany-500' 
                    : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500'
                }`}
              >
                AUCTION
              </button>
              <button
                type="button"
                onClick={() => setFilterBidding(filterBidding === 'false' ? 'all' : 'false')}
                className={`h-8 font-mono text-[11px] rounded-none border transition-colors ${
                  filterBidding === 'false' 
                    ? 'bg-tiffany-500 text-black border-tiffany-500' 
                    : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500'
                }`}
              >
                FIXED PRICE
              </button>
              <button
                type="button"
                onClick={() => setFilterStock(filterStock === 'all' ? 'instock' : 'all')}
                className={`h-8 font-mono text-[11px] rounded-none border transition-colors flex items-center justify-center gap-1.5 px-2 ${
                  filterStock === 'all' 
                    ? 'bg-tiffany-500 text-black border-tiffany-500' 
                    : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500'
                }`}
                data-testid="include-sold-toggle"
              >
                <span className={`w-3 h-3 border rounded-sm flex items-center justify-center ${
                  filterStock === 'all' ? 'bg-black border-black' : 'border-zinc-500'
                }`}>
                  {filterStock === 'all' && (
                    <svg className="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                INCLUDE SOLD
              </button>
            </div>
            
            {/* Sort Dropdown */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 sm:w-32 bg-zinc-900 border-zinc-700 text-white font-mono text-[11px] rounded-none focus:border-tiffany-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="default" className="font-mono text-xs">DEFAULT</SelectItem>
                <SelectItem value="price-low" className="font-mono text-xs">PRICE: LOW</SelectItem>
                <SelectItem value="price-high" className="font-mono text-xs">PRICE: HIGH</SelectItem>
                <SelectItem value="newest" className="font-mono text-xs">NEWEST</SelectItem>
                <SelectItem value="a-z" className="font-mono text-xs">A-Z</SelectItem>
                <SelectItem value="z-a" className="font-mono text-xs">Z-A</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Category Filter Buttons - 5 columns grid (ALL + 4 categories) */}
          <div className="grid grid-cols-5 gap-1.5">
            <button
              type="button"
              onClick={() => setSelectedCategory('all')}
              className={`h-8 font-mono text-[11px] rounded-none border transition-colors ${
                selectedCategory === 'all' 
                  ? 'bg-tiffany-500 text-black border-tiffany-500' 
                  : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500'
              }`}
            >
              ALL
            </button>
            {categories.slice(0, 4).map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(selectedCategory === cat ? 'all' : cat)}
                className={`h-8 font-mono text-[11px] rounded-none border transition-colors uppercase ${
                  selectedCategory === cat 
                    ? 'bg-tiffany-500 text-black border-tiffany-500' 
                    : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </form>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-tiffany-500 font-mono cursor-blink">LOADING</div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 font-mono">NO PRODUCTS FOUND</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-px bg-zinc-800 border border-zinc-800" data-testid="products-grid">
            {visibleProducts.map((product) => {
              const isComingSoon = product.visibility === 'coming_soon';
              
              return (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className={`group bg-[#050505] p-2 sm:p-6 hover:bg-zinc-900/50 transition-colors duration-300 relative ${isUnavailable(product) ? 'opacity-70' : ''}`}
                data-testid={`product-card-${product.id}`}
              >
                {/* Coming Soon Badge */}
                {isComingSoon && (
                  <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10">
                    <Badge className="bg-yellow-500 text-black rounded-none border-0 font-mono text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> COMING SOON
                    </Badge>
                  </div>
                )}
                
                {/* Featured Badge */}
                {product.is_featured && (
                  <div className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10">
                    <Badge className="bg-yellow-500 text-black rounded-none border-0 font-mono text-xs flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" /> FEATURED
                    </Badge>
                  </div>
                )}
                
                {/* Product Image - with placeholder */}
                <div className={`aspect-square mb-2 sm:mb-4 overflow-hidden bg-zinc-900 ${isUnavailable(product) ? 'grayscale' : ''}`}>
                  <img
                    src={getProductImage(product)}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = DEFAULT_PRODUCT_IMAGE;
                    }}
                  />
                </div>

                {/* Badges - Only show auction badges if: is_bidding=true, NOT processed, AND has stock */}
                <div className="flex flex-wrap gap-1 sm:gap-2 mb-1 sm:mb-3">
                  {product.is_bidding && !product.auction_processed && product.stock > 0 && !isComingSoon && (
                    <span className="bg-tiffany-500 text-black font-bold uppercase tracking-wider text-[8px] sm:text-[10px] px-1 sm:px-2 py-0.5 flex items-center gap-0.5 sm:gap-1">
                      <Gavel className="w-2 h-2 sm:w-3 sm:h-3" />
                      AUCTION
                    </span>
                  )}
                  {product.bid_end_time && !product.auction_processed && product.stock > 0 && formatTimeRemaining(product.bid_end_time) !== 'ENDED' && !isComingSoon && (
                    <span className="bg-zinc-800 text-zinc-300 font-mono text-[8px] sm:text-[10px] px-1 sm:px-2 py-0.5 flex items-center gap-0.5 sm:gap-1">
                      <Clock className="w-2 h-2 sm:w-3 sm:h-3" />
                      {formatTimeRemaining(product.bid_end_time)} • {formatAuctionEndTime(product.bid_end_time)}
                    </span>
                  )}
                </div>

                {/* Product Info */}
                <div className="flex items-center gap-2 flex-wrap">
                  {product.brand && product.brand !== 'None' && (
                    <span 
                      className="font-mono text-[8px] sm:text-[10px] px-1.5 py-0.5 uppercase tracking-wider"
                      style={{ 
                        backgroundColor: brandColors[product.brand] || '#2DD4BF',
                        color: ['#000000', '#1F2937', '#111827'].includes(brandColors[product.brand]) ? '#fff' : '#000'
                      }}
                      data-testid={`brand-label-${product.id}`}
                    >
                      {product.brand}
                    </span>
                  )}
                  <span className="text-zinc-500 font-mono text-[9px] sm:text-xs uppercase tracking-wider">
                    {product.category}
                  </span>
                </div>
                <h3 className={`font-sans text-sm sm:text-xl mt-1 sm:mt-2 mb-2 sm:mb-3 group-hover:text-tiffany-500 transition-colors leading-snug uppercase ${isUnavailable(product) ? 'text-zinc-500 line-through' : 'text-white'}`}>
                  {product.name}
                </h3>

                {/* Pricing - hidden for coming soon and unavailable items */}
                {!isUnavailable(product) && !isComingSoon ? (
                  <div className="flex flex-wrap items-baseline gap-1 sm:gap-3">
                    {product.is_bidding && product.current_bid ? (
                      <>
                        <span className="font-mono text-tiffany-500 text-sm sm:text-lg">
                          ${product.current_bid.toFixed(2)}
                        </span>
                        <span className="font-mono text-zinc-500 text-[10px] sm:text-sm">
                          {product.bid_count} bids
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-mono text-white text-sm sm:text-lg">
                          {(() => {
                            const variantPrices = product.has_variants && product.variants?.length > 0
                              ? product.variants.filter(v => v.price != null).map(v => v.price)
                              : [];
                            if (variantPrices.length > 0) {
                              const minP = Math.min(product.price, ...variantPrices);
                              const maxP = Math.max(product.price, ...variantPrices);
                              if (minP !== maxP) return `$${minP.toFixed(0)} - $${maxP.toFixed(0)}`;
                            }
                            return `$${product.price.toFixed(2)}`;
                          })()}
                        </span>
                        {/* BLKCRD Member Price */}
                        {userProfile?.has_blkcrd && !product.is_gift_card && !product.is_blkcrd && !product.is_bidding && (
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-purple-400 text-[10px] sm:text-xs">
                              BLKCRD ${(product.price * 0.85).toFixed(2)}
                            </span>
                            <span className="font-mono text-green-500 text-[10px] sm:text-xs">
                              -${(product.price * 0.15).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {product.is_bidding && product.buy_now_price && !product.auction_processed && product.stock > 0 && (
                      <span className="font-mono text-zinc-500 text-[9px] sm:text-sm flex items-center gap-0.5 sm:gap-1">
                        <Tag className="w-2 h-2 sm:w-3 sm:h-3" />
                        BIN: ${product.buy_now_price.toFixed(2)}
                      </span>
                    )}
                  </div>
                ) : isComingSoon ? (
                  <p className="font-mono text-sm text-yellow-500 mt-1">PRICE TBD</p>
                ) : null}

                {/* Stock indicator - only for non-auction items, not for coming soon */}
                {product.stock <= 3 && product.stock > 0 && !isEndedAuction(product) && !product.is_bidding && !isComingSoon && (
                  <p className="font-mono text-xs text-red-500 mt-2">
                    ONLY {product.stock} LEFT
                  </p>
                )}
                
                {/* Status for ended auctions */}
                {isEndedAuction(product) && (
                  <p className="font-mono text-lg text-zinc-500 mt-3 uppercase tracking-wider text-center">
                    {getAuctionEndStatus(product)}
                  </p>
                )}
                
                {/* Status for sold auctions (auction_result is sold/buy_now_sold) */}
                {isSoldAuction(product) && (
                  <p className="font-mono text-lg text-zinc-500 mt-3 uppercase tracking-wider text-center">
                    SOLD OUT
                  </p>
                )}
                
                {/* Status for sold out items (stock=0) that aren't already handled above */}
                {product.stock === 0 && !isEndedAuction(product) && !isSoldAuction(product) && (
                  <div className="mt-3 text-center">
                    <p className="font-mono text-lg text-zinc-500 uppercase tracking-wider">
                      SOLD OUT
                    </p>
                    {/* Notify Me button for non-auction, non-one-time items */}
                    {!product.is_bidding && !product.is_one_time && (
                      restockSubscriptions.includes(product.id) ? (
                        <div className="font-mono text-xs text-tiffany-500 mt-2 flex items-center justify-center gap-1">
                          <BellRing className="w-3 h-3" />
                          NOTIFICATIONS ON
                        </div>
                      ) : (
                        <button
                          onClick={(e) => handleRestockSubscribe(e, product.id, product.name)}
                          disabled={subscribingProduct === product.id}
                          className="font-mono text-xs text-zinc-400 hover:text-tiffany-500 mt-2 flex items-center justify-center gap-1 mx-auto transition-colors"
                          data-testid={`notify-btn-${product.id}`}
                        >
                          <Bell className="w-3 h-3" />
                          {subscribingProduct === product.id ? 'SUBSCRIBING...' : 'NOTIFY ME'}
                        </button>
                      )
                    )}
                    {/* One-time item indicator */}
                    {product.is_one_time && (
                      <p className="font-mono text-xs text-zinc-600 mt-1">ONE TIME ITEM</p>
                    )}
                  </div>
                )}

                {/* Hover border effect */}
                <div className="absolute inset-0 border border-transparent group-hover:border-tiffany-500/50 pointer-events-none transition-colors duration-300"></div>
              </Link>
            );
            })}
          </div>
        )}
        
        {/* Infinite Scroll Sentinel - triggers load when visible */}
        {!loading && hasMore && (
          <div 
            ref={loadMoreRef}
            className="flex justify-center py-8"
            data-testid="infinite-scroll-trigger"
          >
            <div className="flex items-center gap-2 text-zinc-500 font-mono text-sm">
              <div className="w-4 h-4 border-2 border-tiffany-500 border-t-transparent rounded-full animate-spin"></div>
              Loading more...
            </div>
          </div>
        )}
        
        {/* Product count indicator */}
        {!loading && products.length > 0 && (
          <div className="text-center text-zinc-600 font-mono text-xs mt-4 mb-8">
            Showing {visibleProducts.length} of {sortedProducts.length} products
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer />
      
      {/* Back to Top Button */}
      <BackToTop />
    </div>
  );
}
